import { type RefObject, useCallback, useLayoutEffect } from "react";
import { TAB_CONSTANTS } from "./constants";
import type { TabItem } from "./types";
import { getActiveButton } from "./utils";

type UseTabsScrollProps = {
  items: TabItem[];
  activeIndex: number;
  rtl: boolean;
  tabButtonRefs: RefObject<Map<string, HTMLButtonElement>>;
  enabled: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
};

export function useTabsScroll({
  items,
  activeIndex,
  rtl,
  tabButtonRefs,
  enabled,
  scrollRef,
}: UseTabsScrollProps) {
  const scrollTabIntoView = useCallback(() => {
    const button = getActiveButton(activeIndex, items, tabButtonRefs);
    const container = scrollRef.current;
    if (!button || !container) return;

    const PEEK_OFFSET = TAB_CONSTANTS.SCROLL_PEEK_OFFSET;

    const buttonRect = button.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const buttonLeft = buttonRect.left - containerRect.left;
    const buttonRight = buttonRect.right - containerRect.left;
    const containerWidth = containerRect.width;

    const isButtonFullyVisible =
      buttonLeft >= 0 && buttonRight <= containerWidth;
    if (isButtonFullyVisible) return;

    let targetScrollLeft = container.scrollLeft;

    if (buttonLeft < 0) {
      targetScrollLeft = container.scrollLeft + buttonLeft - PEEK_OFFSET;
    } else if (buttonRight > containerWidth) {
      targetScrollLeft =
        container.scrollLeft + (buttonRight - containerWidth) + PEEK_OFFSET;
    }

    container.scrollTo({
      left: targetScrollLeft,
      behavior: "smooth",
    });
  }, [activeIndex, items, tabButtonRefs, scrollRef, rtl]);

  useLayoutEffect(() => {
    if (!enabled) return;
    scrollTabIntoView();
  }, [enabled, activeIndex, items, scrollTabIntoView]);
}
